import { useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, Show } from "solid-js";

type Mode = "user" | "admin";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = createSignal<Mode>("user");
  const [value, setValue] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const nextParam = () => (typeof params.next === "string" ? params.next : "/dashboard");
  const errParam = () => (typeof params.err === "string" ? params.err : null);
  // Break-glass admin login is hidden from visitors. The owner reaches it via
  // /login?admin=1; the tab (and admin mode) stays out of the default view.
  const adminEnabled = () => params.admin === "1" || params.admin === "true";

  const goNext = () => {
    const next = nextParam();
    navigate(next.startsWith("/") ? next : "/dashboard", { replace: true });
  };

  const submit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    const v = value().trim();
    if (!v) return;
    setSubmitting(true);
    try {
      if (mode() === "user") {
        // Free access: leaving an email signs the visitor in immediately.
        const res = await fetch("/api/auth/signup", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: v }),
        });
        if (res.status === 400) {
          setError("That email doesn't look right. Check it and try again.");
        } else if (res.status === 429) {
          setError("Too many tries. Wait a minute and try again.");
        } else if (!res.ok) {
          setError(`HTTP ${res.status}`);
        } else {
          goNext();
        }
      } else {
        const res = await fetch("/api/auth/admin", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: v }),
        });
        if (res.status === 401) {
          setError("invalid admin token");
        } else if (res.status === 429) {
          setError("too many attempts; wait a minute");
        } else if (!res.ok) {
          setError(`HTTP ${res.status}`);
        } else {
          goNext();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section class="login-page">
      <div class="login-card">
        <div class="login-eyebrow">FREE ACCESS</div>
        <h2 class="login-title">Fortune 500 Business Models portal</h2>

        <Show when={errParam() === "link_invalid"}>
          <div class="login-banner">
            That sign-in link expired or was already used. Enter your email below to get in.
          </div>
        </Show>

        <Show when={adminEnabled()}>
          <div class="login-tabs">
            <button
              type="button"
              class="login-tab"
              classList={{ active: mode() === "user" }}
              onClick={() => { setMode("user"); setError(null); }}
            >
              email
            </button>
            <button
              type="button"
              class="login-tab"
              classList={{ active: mode() === "admin" }}
              onClick={() => { setMode("admin"); setError(null); }}
            >
              admin
            </button>
          </div>
        </Show>

        <form class="login-form" onSubmit={submit}>
          <label class="login-label">
            <Show when={mode() === "user"} fallback={<span>Admin token</span>}>
              <span>Email</span>
            </Show>
            <input
              class="login-input"
              type={mode() === "admin" ? "password" : "email"}
              autocomplete={mode() === "admin" ? "off" : "email"}
              value={value()}
              onInput={(e) => setValue(e.currentTarget.value)}
              placeholder={mode() === "user" ? "you@company.com" : "ADMIN_TOKEN"}
              disabled={submitting()}
              required
            />
          </label>
          <Show when={error()}>
            <div class="login-error">{error()}</div>
          </Show>
          <button class="login-submit" type="submit" disabled={submitting() || !value().trim()}>
            {submitting() ? "..." : mode() === "admin" ? "Sign in" : "Get free access"}
          </button>
          <Show when={mode() === "user"}>
            <p class="login-fine">
              All 500 firms are free. Leave your email and you're in, with no password and no card.
            </p>
          </Show>
        </form>
      </div>
    </section>
  );
}
